import { PlusIcon } from "@heroicons/react/solid";
import { Breadcrumbs } from "components/breadcrumbs";
import Button from "components/button";
import { Link, useLocation, useParams } from "react-router-dom";
import { Filter, EventAddonCard, EventAddonTable } from "./components";
import { useTable, useUpdate } from "@refinedev/core";
import { useMemo } from "react";
import { FullScreenLoading } from "components/fullScreenLoading";
import toast from "react-hot-toast";
import { isHaveActionPermission } from "utills/helpers";
import { DEFAULT_PAGE_SIZE } from "utills/constants";

type VariantQuantityKey =
  | "stock"
  | "stock_total"
  | "stock_remaining"
  | "remaining_stock"
  | "remaining";

interface IAddonVariant {
  stock?: number | string | null;
  stock_total?: number | string | null;
  stock_remaining?: number | string | null;
  remaining_stock?: number | string | null;
  remaining?: number | string | null;
}

interface IEventAddon {
  id: string;
  name?: string;
  name_en: string;
  name_vi: string;
  desc?: string;
  desc_en: string;
  desc_vi: string;
  base_price: number;
  stock_total: number | null;
  stock_remaining: number | null;
  status: string;
  type: string;
  scope: string;
  position: number;
  required: boolean;
  is_sold_out: boolean;
  qty_mode: string;
  qty_min: number;
  qty_max: number;
  has_note: boolean;
  note_required: boolean;
  low_stock_threshold: number;
  variants?: IAddonVariant[];
}

const toFiniteNumber = (value: unknown) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string" && value.trim() !== "") {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
  }

  return null;
};

const getVariantQuantity = (
  variant: IAddonVariant,
  keys: VariantQuantityKey[],
) => {
  for (const key of keys) {
    const quantity = toFiniteNumber(variant[key]);

    if (quantity !== null) {
      return quantity;
    }
  }

  return null;
};

const getVariantQuantityTotal = (
  variants: IAddonVariant[],
  keys: VariantQuantityKey[],
) => {
  let hasQuantity = false;
  const total = variants.reduce((sum, variant) => {
    const quantity = getVariantQuantity(variant, keys);

    if (quantity === null) {
      return sum;
    }

    hasQuantity = true;
    return sum + quantity;
  }, 0);

  return hasQuantity ? total : null;
};

const applyVariantStockTotals = (addon: IEventAddon) => {
  const variants = addon.variants ?? [];

  if (addon.type !== "options" || variants.length === 0) {
    return addon;
  }

  const variantStockTotal = getVariantQuantityTotal(variants, [
    "stock_total",
    "stock",
  ]);
  const variantStockRemaining = getVariantQuantityTotal(variants, [
    "stock_remaining",
    "remaining_stock",
    "remaining",
    "stock",
  ]);

  return {
    ...addon,
    stock_total: variantStockTotal ?? addon.stock_total,
    stock_remaining: variantStockRemaining ?? addon.stock_remaining,
  };
};

const getEventIdFromSearch = (search: string) => {
  const params = new URLSearchParams(search);
  const directEventId = params.get("event_id");

  if (directEventId) {
    return directEventId;
  }

  for (const [key, value] of params.entries()) {
    if (key.endsWith("[field]") && value === "event_id") {
      const filterKey = key.replace("[field]", "");
      return params.get(`${filterKey}[value]`) || "";
    }
  }

  return "";
};

export const EventAddonList: React.FC = () => {
  const { event_id } = useParams<{ event_id: string }>();
  const location = useLocation();
  const { mutate: updateMutation, isLoading: updateMutationIsLoading } = useUpdate();
  const eventIdFromSearch = useMemo(
    () => getEventIdFromSearch(location.search),
    [location.search],
  );
  const selectedEventId = event_id || eventIdFromSearch;
  const canFetchAddons = !!selectedEventId;

  const { tableQuery, filters, setFilters } = useTable<IEventAddon>({
    resource: "ws-event-addons/",
    syncWithLocation: true,
    filters: {
      initial: event_id
        ? [{ field: "event_id", operator: "eq", value: event_id }]
        : [],
    },
    pagination: {
      mode: "off",
      pageSize: DEFAULT_PAGE_SIZE,
    },
    meta: {
      resource: "addons",
      offset: true,
    },
    queryOptions: {
      enabled: canFetchAddons,
    } as any,
  });

  const addons = useMemo(() => {
    return (tableQuery.data?.data ?? []).map((addon) =>
      applyVariantStockTotals({
        ...addon,
        name_vi: addon.name_vi || addon.name || "",
        name_en: addon.name_en || addon.name || "",
        desc_vi: addon.desc_vi || addon.desc || "",
        desc_en: addon.desc_en || addon.desc || "",
        is_sold_out: addon.is_sold_out ?? false,
      }),
    );
  }, [tableQuery.data?.data]);
  
  const totalAddons = tableQuery.data?.total ?? addons.length;

  const currentFilterValues = useMemo(() => {
    const logicalFilters = filters.flatMap((item) =>
      "field" in item ? item : [],
    );

    return {
      q: logicalFilters.find((item) => item.field === "q")?.value || "",
      event_id:
        logicalFilters.find((item) => item.field === "event_id")?.value ||
        selectedEventId ||
        "",
      type: logicalFilters.find((item) => item.field === "type")?.value || "",
    };
  }, [filters, selectedEventId]);

  const _toggleSoldOut = (id: string, isSoldOut: boolean) => {
    if (!isHaveActionPermission("addons", "events", "edit")) return;
    const nextSoldOutStatus = !isSoldOut;

    if (
      window.confirm(
        `Are you sure you want to ${isSoldOut ? "mark this addon as available" : "mark this addon as sold out"}?`,
      )
    ) {
      updateMutation(
        {
          resource: "ws-event-addons",
          id,
          meta: {
            URLSuffix: "sold-out",
            ignoreResourceWrapper: true,
          },
          values: {
            is_sold_out: nextSoldOutStatus,
          },
        },
        {
          onSuccess: () => {
            toast.success("Update successfully");
            tableQuery.refetch();
          },
        },
      );
    }
  };

  return (
    <div className="wrapper">
      <Breadcrumbs
        {...(isHaveActionPermission("addons", "events", "create") && {
          additionalLabel: (
            <Link to="/events/addons/create">
              <Button>
                <span className="sr-only">Create New Addon</span>
                <PlusIcon className="w-4 h-4 text-white" />{" "}
                <span className="hidden sm:block sm:ml-2">
                  Create New Addon
                </span>
              </Button>
            </Link>
          ),
        })}
      />
      <div className="box-container">
        <Filter
          currentFilterValues={currentFilterValues}
          setFilters={setFilters}
        />
      </div>
      <div className="box-container">
        {!canFetchAddons ? (
          <div className="text-center">Please select an event</div>
        ) : null}
        {canFetchAddons && !addons.length && !tableQuery.isFetching && (
          <div className="text-center">No data</div>
        )}
        {addons.length > 0 ? (
          <>
            <div className="sm:hidden">
              <EventAddonCard
                data={addons}
                pageSize={addons.length}
                currentPage={1}
                _toggleSoldOut={_toggleSoldOut}
              />
            </div>
            <div className="hidden sm:block">
              <EventAddonTable
                data={addons}
                pageSize={addons.length}
                currentPage={1}
                _toggleSoldOut={_toggleSoldOut}
              />
            </div>
          </>
        ) : (
          <></>
        )}
        {canFetchAddons && addons.length > 0 ? (
          <p className="mt-6 text-sm font-medium text-gray-700">
            Total: <span>{addons.length}</span> / <span>{totalAddons}</span>
          </p>
        ) : null}
      </div>
      {(canFetchAddons && tableQuery.isLoading) || updateMutationIsLoading ? (
        <FullScreenLoading />
      ) : (
        <></>
      )}
    </div>
  );
};
